import { LitElement, html, utils, css, ifDefined } from '@hh/global.js'
const { prefersReducedMotion } = utils

class HHDrawer extends LitElement {
  static properties = {
    _trapFocus: {
      state: true,
    },
    _open: {
      state: false,
    },
    label: {
      type: String,
    },
    modal: {
      type: Boolean,
    },
  }

  constructor() {
    super()

    this.handleBodyClick = this.handleBodyClick.bind(this)

    document.addEventListener('openDrawer', (e) => {
      this.open(e)
    })
    this.querySelectorAll('.js-additional-close')?.forEach((el) => {
      el.addEventListener('click', this.close)
    })

    document.addEventListener('keydown', (e) => {
      if (
        document.body.classList.contains('mobile-menu-open') &&
        e.code.toUpperCase() == 'ESCAPE'
      ) {
        document.querySelector("hh-drawer[data-drawer='mobile-menu']").close()
      } else if (
        e.code.toUpperCase() == 'ESCAPE' &&
        this.closest('.drawer').classList.contains('drawer-is-active')
      ) {
        this.close()
      }
    })
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.removeEventListener('click', this.handleBodyClick)
  }

  handleBodyClick(e) {
    // Keep the 3D drawer open while interacting with the AR modal
    const threeDDrawer = document.querySelector('[data-drawer="3d-ar"]')
    const arModal = document.querySelector('.js-ar-modal')
    if (
      this === threeDDrawer &&
      arModal?.classList.contains('drawer-is-active')
    ) {
      return
    }

    // Keep mobile menu open if clicking inside the orbe modal
    const orbeModal = document.querySelector('#md-app-embed__modal')
    if (orbeModal && orbeModal.contains(e.target)) {
      return
    }

    // Keep the drawer open if clicking on a store pin in the store locator map
    const storeLocatorPin = e.target?.closest('[data-store-locator-pin]')
    if (storeLocatorPin) {
      const drawerKey = `store--${storeLocatorPin.getAttribute('data-store-handle')}`
      if (drawerKey === this.dataset.drawer) {
        return
      }
    } else if (
      this.classList.contains('store-locator-page__store-drawer') &&
      !e.target.closest('.drawer__close')
    ) {
      return
    }

    if (
      (!this.contains(e.target) &&
        this._open &&
        !this.trigger.contains(e.target)) ||
      e?.code?.toUpperCase() === 'ESCAPE'
    ) {
      this.close()
    }
  }

  open(e) {
    if (e.detail.key == this.dataset.drawer) {
      if (this._open) {
        this.close()
        return
      }

      // Only the opener is remembered, so focus returns to it even when the drawer
      // is closed by a toggle inside the drawer itself (e.g. the mobile menu close)
      this.trigger = e.target?.closest('.js-drawer-trigger')

      if (e.detail.key.indexOf('store') > -1) {
        document.body.classList.add('scroll-lock-mobile')
      } else {
        document.body.classList.add('scroll-lock')
      }

      if (
        e.detail.key != 'media-zoom' &&
        e.detail.key != 'search' &&
        e.detail.key.indexOf('store') < 0
      ) {
        document.querySelector('.js-overlay').classList.add('active')
        if (this.dataset.drawer === 'ar-modal') {
          document
            .querySelector('.js-overlay')
            .classList.add('overlay-secondary')
        }
      }

      if (e.detail.key == 'media-zoom') {
        document
          .querySelector('hh-zoom-gallery')
          ?.swiper?.slideTo(e.detail.index - 1, 0)
      }

      this.closest('.drawer').classList.add('drawer-is-active')
      this._open = true

      if (!prefersReducedMotion()) {
        document
          .querySelectorAll('hh-vertical-carousel')
          .forEach((el) => el.pause())
      }

      if (e.detail.key == 'mobile-menu') {
        document.body.classList.add('mobile-menu-open')
      }

      if (e.detail.key == 'search') {
        setTimeout(() => {
          this.focusWhenVisible(document.querySelector('#search-id'))
        }, 100)
      }

      if (e.detail.key === '3d-ar' || e.detail.key === 'ar-modal') {
        setTimeout(() => {
          this.querySelector('.js-focus-entry').focus()
        }, 100)
      }

      if (this.modal) {
        this.setBackgroundInert(true)
        setTimeout(() => {
          // Prefer a visible control marked as the entry point; hh-button renders its
          // real button in shadow DOM, so focus that instead of the host
          const initialFocus = this.querySelector('[data-drawer-initial-focus]')
          const target =
            initialFocus?.shadowRoot?.querySelector('button, a') ||
            initialFocus ||
            this.querySelector('[slot="close"]')
          // Keep focus that was already moved inside (e.g. the search input)
          this.focusWhenVisible(target, 3000, { fallback: true })
        }, 100)
      }
    }
  }

  // The drawer can still be visibility: hidden when focus is requested (e.g. on first open),
  // so retry until focus lands inside, the drawer closes, or the timeout passes
  // `fallback` gives way to any focus that lands inside first, so it never overrides
  // a more specific target retrying in parallel (e.g. the search input)
  focusWhenVisible(el, timeout = 3000, { fallback = false } = {}) {
    if (!el) return
    const start = Date.now()
    const tryFocus = () => {
      if (!this._open) return
      if (fallback && this.contains(document.activeElement)) return
      el.focus()
      if (this.contains(document.activeElement)) return
      if (Date.now() - start > timeout) return
      setTimeout(tryFocus, 50)
    }
    tryFocus()
  }

  setBackgroundInert(inert) {
    if (!inert) {
      this._inertElements?.forEach((el) => {
        el.inert = false
      })
      this._inertElements = []
      return
    }

    this._inertElements = []
    let el = this.closest('.drawer')
    while (el && el !== document.body && el.parentElement) {
      for (const sibling of el.parentElement.children) {
        if (sibling === el || sibling.inert) continue
        if (['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) continue
        // Backdrop has no focusable content and must stay clickable to close the search
        if (sibling.classList.contains('header-search-backdrop')) continue
        // The Orbe country modal can be opened from inside the mobile menu
        if (sibling.id === 'md-app-embed__modal') continue
        sibling.inert = true
        this._inertElements.push(sibling)
      }
      el = el.parentElement
    }
  }

  close(e) {
    e?.preventDefault()

    if (this.dataset.drawer !== 'ar-modal') {
      document.body.classList.remove(
        'scroll-lock',
        'scroll-lock-mobile',
        'mobile-menu-open'
      )
    }
    document.querySelector('.js-overlay').classList.remove('active')
    if (this.dataset.drawer === 'ar-modal') {
      document
        .querySelector('.js-overlay')
        .classList.remove('overlay-secondary')
    }

    this.closest('.drawer').classList.remove('drawer-is-active')
    this._open = false
    if (!prefersReducedMotion()) {
      document
        .querySelectorAll('hh-vertical-carousel')
        .forEach((el) => el.play())
    }

    document
      .querySelectorAll('.js-drawer-trigger')
      .forEach((el) => el.classList.remove('active'))

    if (this.modal) {
      this.setBackgroundInert(false)
    }

    setTimeout(() => {
      const triggerButton =
        this.trigger?.shadowRoot?.querySelector('button') ||
        (this.trigger?.tagName === 'BUTTON' ? this.trigger : null)
      if (triggerButton) {
        triggerButton.focus()
      } else if (this.modal) {
        this.trigger?.focus()
      }
    }, 100)
  }

  render() {
    return html`<div
      role="dialog"
      aria-modal="true"
      aria-label="${ifDefined(this.label)}"
      @keyup="${this.handleBodyClick}"
      style="height: 100%;"
    >
      <hh-focus-trap active="true">
        <slot name="close" @click="${this.close}"></slot>
        <slot name="body" style="height: 100%;"></slot>
      </hh-focus-trap>
    </div> `
  }

  static styles = [
    css`
      :host([data-drawer='gift-wrapping']) div {
        height: auto !important;
      }
    `,
  ]
}

customElements.define('hh-drawer', HHDrawer)
